import type { ExportJob } from '../api/contracts'
import { EXPORT_MIME_TYPES, fetchExportFile } from './exportJobs'

type ShareFile = Pick<ExportJob, 'export_id' | 'file_name' | 'format'>

/**
 * Whether the browser can hand a file to the device's share sheet (Chrome
 * and Edge on Windows and the Mac, Safari, phones): `navigator.share` with
 * `files`. Firefox on a computer can't.
 */
export function canShareFiles(nav: Navigator = navigator): boolean {
  if (typeof nav.share !== 'function' || typeof nav.canShare !== 'function') return false
  try {
    return nav.canShare({
      files: [new File([''], 'export.pdf', { type: EXPORT_MIME_TYPES.pdf })],
    })
  } catch {
    return false
  }
}

/** Why a format's Share is off in this browser. */
export const CANT_SHARE_FILES_REASON =
  "This browser can't share files. Export the file and attach it instead."

/**
 * What came of sharing a file: `shared`, `canceled` (the person closed the
 * share sheet), or `needs-click`: the browser opens a share sheet only
 * within a few seconds of a click, and the file took longer to make, so it
 * waits for another click (`file` in hand, so that one shares at once).
 */
export type ShareOutcome =
  | { outcome: 'shared' }
  | { outcome: 'canceled' }
  | { outcome: 'needs-click'; file: File }

/** Open the device's share sheet with a file already read. */
export async function shareFile(file: File): Promise<ShareOutcome> {
  try {
    await navigator.share({ files: [file], title: file.name })
    return { outcome: 'shared' }
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') return { outcome: 'canceled' }
    if (error instanceof DOMException && error.name === 'NotAllowedError') {
      return { outcome: 'needs-click', file }
    }
    throw error
  }
}

/** Read a finished export and open the device's share sheet with it. */
export async function shareExport(job: ShareFile): Promise<ShareOutcome> {
  return shareFile(await fetchExportFile(job))
}
